import HelpdeskGraphQL from "./HelpdeskGraphQL";
import axios from "./Axios";
import {HELPDESK_SERVICE_URL} from "../config";

const conversationSelector = `
    id
    lastMessageText
    lastMessageSenderId
    lastMessageSenderType
    createdAt
    updatedAt
    assignee
    priority
    isArchived
    status
    name
    cookieId
    inAppMessageStatus
    inAppMessageType
    isInAppMessage
    subject
    userUnReadMessagesCount
    lastMessageTime
`

class Conversation extends HelpdeskGraphQL {
    static async getConversations({ cookieId, onlyForCurrentDevice = true, appClientId, brandId }) {
        const queryName = 'conversationsByCookie'

        const query = `
            query conversations($cookieId: ID, $onlyMessenger: Boolean, $onlyForCurrentDevice: Boolean, $appClientId: String, $brandId: String) {
                ${queryName}(cookieId: $cookieId, onlyMessenger: $onlyMessenger, onlyForCurrentDevice: $onlyForCurrentDevice, brandId: $brandId, appClientId: $appClientId) {
                    ${conversationSelector}
                }
            }
        `
        const variables = {
            cookieId,
            onlyMessenger: false,
            onlyForCurrentDevice,
            appClientId,
            brandId,
        }

        return await this.run({ query, variables, queryName })
    }

    static async createConversation({
        id, cookieId,
        lastMessageText,
        createdAt,
        updatedAt,
        type,
        message,
        scenarioName,
        scenarioId,
        tags,
        appClientId,
        lastMessageSenderId,
        lastMessageSenderType,
        conversationProperties,
        subject,
        sessionId,
        }) {
        const mutationName = 'createConversation'

        const query = `
            mutation createConversation($conversation: ConversationInput) {
                ${mutationName}(conversation: $conversation) {
                    ${conversationSelector}
                }
            }
        `
        if(message.createdAt) {
          delete message.createdAt
        }
        const variables = {
            conversation: {
                id, cookieId, lastMessageText, createdAt, updatedAt, type, message, appClientId,
                lastMessageSenderId, lastMessageSenderType,scenarioId, scenarioName, tags, conversationProperties,
                sessionId
            }
        }
        if(subject) {
            variables.conversation.subject = subject;
        }

        return await this.run({ query, variables, queryName: mutationName })
    }

    static async updateConversationMessage({
        id, text, time,
        lastMessageSenderId,
        lastMessageSenderType,
        }) {
        const mutationName = 'updateConversationMessage'

        const query = `
            mutation updateConversationMessage($id: ID, $time: Float, $text: String, $lastMessageSenderId: ID, $lastMessageSenderType: String) {
                ${mutationName}(id: $id, time: $time, text: $text, lastMessageSenderId: $lastMessageSenderId, lastMessageSenderType: $lastMessageSenderType) {
                    id
                }
            }
        `
        const variables = {
            id, text, time,
            lastMessageSenderId,
            lastMessageSenderType
        }

        return await this.run({ query, variables, queryName: mutationName })
    }
    static async updateConversationProperties({ id, properties }) {
        const {data, errors} = await axios()({
            url: HELPDESK_SERVICE_URL + '/conversation-properties/'+id,
            method: 'POST',
            responseType: 'json',
            data: {
                id,
                properties
            }
        });
        if (errors && errors.length) {
            throw errors[0]
        }
        return data;
    }

    static async updateConversationPriority({ id, priority, operatorId }) {
        const mutationName = 'updateConversationPriority'

        const query = `
            mutation updateConversationPriority($id: ID, $operatorId: ID, $priority: String) {
                ${mutationName}(id: $id, operatorId: $operatorId, priority: $priority) {
                    id
                }
            }
        `
        const variables = {
            id, priority, operatorId
        }

        return await this.run({ query, variables, queryName: mutationName })
    }

    static async updateConversationStatus({ id, status, operatorId, askForRating = false }) {
        const mutationName = 'updateConversationStatus'

        const query = `
            mutation updateConversationStatus($id: ID, $operatorId: ID, $status: String, $askForRating: Boolean) {
                ${mutationName}(id: $id, operatorId: $operatorId, status: $status, askForRating: $askForRating) {
                    id
                }
            }
        `
        const variables = {
            id, status, operatorId, askForRating
        }

        return await this.run({ query, variables, queryName: mutationName })
    }

    static async updateConversationAssignee({ id, assignee, operatorId, currencySymbol }) {
        const mutationName = 'updateConversationAssignee'

        const query = `
            mutation updateConversationAssignee($addAssignee: AssigneeInput!, $useTeamAlg: Boolean) {
                ${mutationName}(addAssignee: $addAssignee, useTeamAlg: $useTeamAlg) {
                    id
                }
            }
        `
        const variables = {
            addAssignee: { id, assignee, operatorId, currencySymbol },
            useTeamAlg: true
        }

        return await this.run({ query, variables, queryName: mutationName })
    }

    static async updateConversationTags(id, tags){
        const queryName = 'updateConversationTags';
        const query = `
			mutation updateConversationTags($id: ID!, $tag: [ID!]){
				${queryName}(id: $id tag: $tag){
                    tags,
                    newTag {
                        tagId,
                        label
                    },
				}
			}
		`
        const variables = {
            id, tag:tags
        }

        try {
            return await this.run({query, variables, queryName});
        }
        catch (error) {
            throw error
        }
    }
    static async updateInAppMessageStatus({ id, status }) {
        const mutationName = 'updateInAppMessageStatus'

        const query = `
            mutation updateInAppMessageStatus($id: ID, $inAppMessageStatus: String) {
                ${mutationName}(id: $id, inAppMessageStatus: $inAppMessageStatus) {
                    id
                }
            }
        `
        const variables = {
            id, inAppMessageStatus: status
        }

        return await this.run({ query, variables, queryName: mutationName })
    }

    static async updateUserUnReadMessagesCountForConversation({ id }) {
        const senderType = 'customer';
        const mutationName = 'updateConversationUserUnReadMessagesCount';
        const time = new Date().getTime();
        const query = `
            mutation updateConversationUserUnReadMessagesCount($id: ID, $time: Float, $senderType: String) {
                ${mutationName}(id: $id, time: $time, senderType: $senderType) {
                    id
                }
            }
        `
        const variables = {
            id, time, senderType
        }

        return await this.run({ query, variables, queryName: mutationName })
    }

    static async updateConversationRating({ id, rating }) {
        const mutationName = 'updateConversationRating'

        const query = `
            mutation updateConversationRating($id: ID!, $rating: Int!) {
                ${mutationName}(id: $id, rating: $rating) {
                    id
                }
            }
        `
        const variables = {
            id, rating
        }

        return await this.run({ query, variables, queryName: mutationName })
    }
    static async updateConversationFeedback({ id, feedback }) {
        const mutationName = 'updateConversationFeedback'

        const query = `
            mutation updateConversationFeedback($id: ID!, $feedback: String!) {
                ${mutationName}(id: $id, feedback: $feedback) {
                    id
                }
            }
        `
        const variables = {
            id, feedback
        }

        return await this.run({ query, variables, queryName: mutationName })
	}
	
	static async runAutomationRule({ conversationId, canExecuteForScenarios = false, canDoAgentAssignment = true, canSendAutoRespondMessages = true }) {
		try {
			const mutationName = 'runAutomationRuleForConversation';

			const query = `
				mutation runAutomationRuleForConversation($conversationId: ID!, $canExecuteForScenarios: Boolean, $canDoAgentAssignment: Boolean, $canSendAutoRespondMessages: Boolean ) {
					${mutationName}(conversationId: $conversationId,  canExecuteForScenarios: $canExecuteForScenarios, canDoAgentAssignment: $canDoAgentAssignment, canSendAutoRespondMessages: $canSendAutoRespondMessages) {
						status
						isArchived
					}
				}
			`;
			const variables = {
                conversationId,
                canExecuteForScenarios,
                canDoAgentAssignment,
                canSendAutoRespondMessages
			};

			return await this.run({
				query,
				variables,
				queryName: mutationName
			});
		} catch (error) {
			throw error;
		}
	}

}

export default Conversation
